import { JSX, ReactNode, useEffect, useState } from "react";
import {
    Grid,
    Box,
    CircularProgress,
    Button
} from '@mui/material';
import { convertCurrencyToSymbol } from "../../../defaults/defaults";
import { useASettingByCode } from "../../global-hooks/default-system-hooks";
import { SubscriptionInvoice } from "../../../types/entities";
import {
    useRecordContext,
    useGetOne
} from 'react-admin';
import { useInvoicePreviewData, useInvoiceGenerator } from "./hooks/subscription-invoice-show-preview.hooks";

function findMonth(num: number): string {
    const months = {
        1: 'Jan',
        2: 'Feb',
        3: 'Mar',
        4: 'Apr',
        5: 'May',
        6: 'Jun',
        7: 'Jul',
        8: 'Aug',
        9: 'Sep',
        10: 'Oct',
        11: 'Nov',
        12: 'Dec'
    };
    return months[num] ?? 'N/A';
}

/** `actions` sit beside the export button, so an invoice's buttons share one row. */
export const SubscriptionInvoiceShowPreview: (props: { actions?: ReactNode }) => JSX.Element = ( props: { actions?: ReactNode } ): JSX.Element => {
    const invoice: SubscriptionInvoice = useRecordContext();
    const { loadingInvoiceData, invoiceData } = useInvoicePreviewData(invoice?.id);
    const { loadingInvoice, generateInvoicePDF } = useInvoiceGenerator(invoice?.id);
    const { loadingSetting, settingValue, loadSettingValue, error: settingError } = useASettingByCode("default_noknok_invoice_html_template");


    function cleanHtmlContent(): string {
        let invoiceTemplateString: string = settingValue;
        const today: Date = new Date(invoiceData?.invoiceCreated);

        const todayString: string = today.getDate() + ' ' + findMonth(today.getMonth() + 1) + ' ' + today.getFullYear();
        if (invoiceTemplateString?.includes('{{invoice_data_table}}')) {
            const htmlString: string = `
                <table cellpadding="0" cellspacing="0" style="margin-left:auto; font-size:12px;">
                    <tbody><tr><td>Invoice #</td><td style="padding-left:10px;"><strong>${ invoiceData?.invoiceId }</strong></td></tr>
                        <tr><td>Date</td><td style="padding-left:10px;">${ todayString }</td></tr>
                        <tr><td>Currency</td><td style="padding-left:10px;">${ invoiceData?.invoiceCurrency?.toUpperCase() + ' (' + convertCurrencyToSymbol(invoiceData?.invoiceCurrency) + ')' }</td></tr>
                    </tbody>
                </table>
            `;
            invoiceTemplateString = invoiceTemplateString.replaceAll('{{invoice_data_table}}', htmlString);
        }
        if (invoiceTemplateString?.includes('{{invoice_status}}')) {
            invoiceTemplateString = invoiceTemplateString.replaceAll('{{invoice_status}}', "<div><span class=\"badge\">" + invoiceData?.invoiceStatus + "</span></div>" );
        }
        if (invoiceTemplateString?.includes('{{invoice_id}}')) {
            invoiceTemplateString = invoiceTemplateString.replaceAll('{{invoice_id}}', invoiceData?.invoiceId);
        }
        if (invoiceTemplateString?.includes('{{customer_billing_address}}')) {
            invoiceTemplateString = invoiceTemplateString.replaceAll('{{customer_billing_address}}', invoiceData?.customerBillingAddress );
        }
        if (invoiceTemplateString?.includes('{{items_table}}')) {
            invoiceTemplateString = invoiceTemplateString.replaceAll('{{items_table}}', invoiceData?.itemsTable );
        }
        if (invoiceTemplateString?.includes('{{totals_data_table}}')) {

            invoiceTemplateString = invoiceTemplateString.replaceAll('{{totals_data_table}}', invoiceData?.totalsData);
        }
        if (invoiceTemplateString?.includes('{{support_email}}')) {
            invoiceTemplateString = invoiceTemplateString.replaceAll('{{support_email}}', invoiceData?.supportEmail );
        }
        if (invoiceTemplateString?.includes('{{logo_source}}')) {
            invoiceTemplateString = invoiceTemplateString.replaceAll('{{logo_source}}', '/images/icon/nn-logo.png');
        }
        return invoiceTemplateString;
    }

    function handleInvoicePDFGeneration(): void {
        generateInvoicePDF();
    }

    return (
        <>
            <Box sx={ {
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                margin: '0 0 20px 0',
            } } >
                { props.actions }

                <Button variant="contained"
                        disabled={ loadingInvoice }
                        onClick={ handleInvoicePDFGeneration }>
                    { "Export Invoice PDF" }
                    { (loadingInvoice) &&
                        <CircularProgress size={ 20 } />
                    }
                </Button>
            </Box>
            <Box sx={ {
                width: '100%',
                minHeight: '70vh',
                background: '#fff',
                padding: '20px'
            } }>
                { (loadingInvoiceData) &&
                    <Box sx={ {
                        border: '1px solid #464545',
                        padding: '20px',
                        borderRadius: '15px',
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        color: '#000'
                    } }>
                        <span>{ "Loading Invoice Preview" }</span>
                        <CircularProgress size={ 30 } />
                    </Box>
                }
                { (!loadingInvoiceData) &&
                    <Box sx={ {
                        border: '1px solid #464545',
                        padding: '20px',
                        borderRadius: '15px',
                        width: '100%',
                        height: '100%',
                        maxWidth: '736.65px',
                        margin: '0 auto'
                    } }>
                        <div className="body__plain"
                             dangerouslySetInnerHTML={ {
                                 __html: cleanHtmlContent()
                             } } />
                    </Box>
                }

            </Box>

        </>
    );
}